import { action } from '@reatom/framework'
import i18next from 'i18next'

import { Result } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { reatomCreateLimitOrder } from '@root/features/trade/limit-orders'
import { tradeSettingsStateAtom } from '@root/features/trade/trade-settings'
import { tradingPairAtom } from '@root/features/trade/trading'
import { tradingWalletAtom } from '@root/features/trade/trading/atoms/trading-wallet'
import { Error } from '@root/shared'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { reatomLimitSellInput } from './reatom-limit-sell-input'

import type { Atom } from '@reatom/framework'
import type { TradeSettings } from '@root/entities/trade-settings'
import type BigNumber from 'bignumber.js'

export const reatomLimitSellControls = ({
  sellAmountCurrencyAtom,
}: {
  sellAmountCurrencyAtom: Atom<BigNumber>
}) => {
  const {
    targetTypeAtom,
    sellLowPercentsAtom,
    sellHighPercentsAtom,
    sellLowUsdAtom,
    sellHighUsdAtom,
    receiveAtom,
    setPriceUsdAction,
    outputSettingsAtom,
  } = reatomLimitSellInput({
    pairAtom: tradingPairAtom,
    sellAmountCurrencyAtom: sellAmountCurrencyAtom,
    walletAtom: tradingWalletAtom,
    initialOrder: undefined,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const createModel = reatomCreateLimitOrder()

  const createOrderAction = action(
    async (
      ctx,
      {
        settingsOverride,
      }: { settingsOverride: TradeSettings.TradeSettings | undefined },
    ) => {
      const pair = ctx.get(tradingPairAtom)
      const wallet = ctx.get(tradingWalletAtom)
      const sellAmount = ctx.get(sellAmountCurrencyAtom)
      const settings = settingsOverride ?? ctx.get(tradeSettingsStateAtom).value
      const limitOrderSettings = ctx.get(outputSettingsAtom)

      if (
        !pair ||
        !wallet ||
        sellAmount.isZero() ||
        !settings ||
        !limitOrderSettings
      ) {
        return Result.fail(Error.UnknownError.fromUnknown('Invalid input'))
      }

      const network = ctx.get(selectedNetworkAtom)

      const price = ctx.get(pair.price)
      const marketCapUsd = ctx.get(pair.marketCapUsd)

      const result = await ctx.schedule(() =>
        createModel.createOrderAction(ctx, {
          type: 'sell',
          currencyAddress: pair.quote.address,
          price,
          settings: settings,
          network,
          pairAddress: pair.address,
          walletId: wallet.id,
          walletAddress: wallet.address,
          marketCapUsd: marketCapUsd,
          valueLow: limitOrderSettings.valueLow,
          valueHigh: limitOrderSettings.valueHigh,
          triggerType: limitOrderSettings.type,
          amount: sellAmount,
          deadline: settings.deadline,
          provider: pair.provider,
        }),
      )

      if (Result.isSuccess(result)) {
        showSnackbarAction(ctx, {
          text: i18next.t('trading.limit-order.order-placed'),
          intent: 'success',
        })
      } else {
        showSnackbarAction(ctx, {
          text: i18next.t('trading.limit-order.order-error'),
          intent: 'error',
        })
      }

      return result
    },
    'createOrderAction',
  )

  return {
    targetTypeAtom,
    sellLowPercentsAtom,
    sellLowUsdAtom,
    sellHighPercentsAtom,
    sellHighUsdAtom,
    receiveAtom,
    createOrderAction,
    createOrderState: createModel.createStateAtom,
    setPriceUsdAction,
  }
}
